import { z } from 'zod';

/**
 * The ceiling on one `?keys=` request. The registry holds ~50 definitions and
 * cannot grow at runtime, so this bound is unreachable in practice — it is here
 * because an unbounded fan-out over a per-key resolver is a request an operator
 * should not be able to author, and it is **refused** rather than clamped.
 */
export const SETTING_KEYS_PARAM_MAX = 100;

/**
 * A set of setting keys that arrives as ONE comma-joined query parameter.
 *
 * It is a **string**, not `z.array(...)`, for the reason `IdsParamSchema` is:
 * Express's `qs` parses one `?keys=x` into a bare string and two into an array,
 * so an array-shaped schema would accept a one-element request only by accident.
 * One spelling, no ambiguity (N7).
 *
 * It deliberately does **not** validate a key's shape. The registry and its
 * `KEY_PATTERN` live in the api, and `SettingsAdminService.declared()` is the one
 * place a key is narrowed — so an unknown key is a 404 from the single authority
 * rather than a 422 from a duplicated pattern that would drift.
 */
export const SettingKeysParamSchema = z
  .string()
  .transform((raw) =>
    raw
      .split(',')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0),
  )
  .pipe(z.array(z.string().min(1).max(200)).min(1).max(SETTING_KEYS_PARAM_MAX));

export type SettingKeysParam = z.infer<typeof SettingKeysParamSchema>;
